import { keyframes, Stack, type StackProps, styled } from "@mui/material";
import type { ReactNode } from "react";

const pulseRing = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.6; /* Start slightly translucent for a smoother fade-in */
  }
  100% {
    transform: scale(1.6); /* Slightly larger peak for better visibility */
    opacity: 0;
  }
`;

interface AnimatedStackProps {
  /**
   * Controls whether the pulse animation is active. When false, rings will fade out smoothly instead of disappearing abruptly.
   * @default true
   */
  isPulsing?: boolean;
  duration?: number;
  /**
   * Border radius for the pulse rings
   * @default "50%" - circular pulse
   * @example "8px" - rounded corners
   * @example "0" - sharp corners (for hexagons/polygons)
   */
  pulseRingBorderRadius?: string;
  /**
   * Custom clip-path for complex shapes (hexagons, trapezoids, etc.)
   * @example "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" - hexagon
   */
  clipPath?: string;
  pulseColor?: string;
}

export interface PulseAnimationContainerProps
  extends AnimatedStackProps,
    StackProps {
  children: ReactNode;
}

const AnimatedStack = styled(Stack, {
  shouldForwardProp: (prop) =>
    ![
      "isPulsing",
      "duration",
      "pulseRingBorderRadius",
      "clipPath",
      "pulseColor",
    ].includes(prop as string),
})<AnimatedStackProps>(
  ({
    isPulsing = true,
    duration = 2,
    pulseRingBorderRadius = "50%",
    clipPath,
    pulseColor = "rgba(25, 118, 210, 0.4)",
  }) => ({
    position: "relative",
    display: "inline-flex",
    overflow: "visible",
    zIndex: 0,

    // 1. Keep pseudo-elements always rendered, control animation state instead
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      backgroundColor: pulseColor,
      borderRadius: pulseRingBorderRadius,
      ...(clipPath && { clipPath }),
      pointerEvents: "none",
      zIndex: -1,

      // 2. Hardware acceleration optimization
      willChange: "transform, opacity",

      // 3. Apply animation when pulsing, remove it when not pulsing to allow transitions
      animation: isPulsing
        ? `${pulseRing} ${duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite`
        : "none",

      // 4. Smoothly hide the rings when pulsing stops instead of snapping out of existence
      transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
      ...(!isPulsing && {
        opacity: 0,
        transform: "scale(1)",
      }),
    },

    // Respect user's motion preferences for accessibility
    "@media (prefers-reduced-motion: reduce)": {
      "&::before, &::after": {
        animation: "none",
        opacity: 0,
      },
    },

    "&::after": {
      // Math expression handles dynamic durations safely
      animationDelay: `${duration / 2}s`,
    },
  }),
);

export function PulseAnimationContainer({
  children,
  isPulsing = true,
  duration = 2,
  pulseRingBorderRadius = "50%",
  clipPath,
  ...props
}: PulseAnimationContainerProps) {
  return (
    <AnimatedStack
      isPulsing={isPulsing}
      duration={duration}
      pulseRingBorderRadius={pulseRingBorderRadius}
      clipPath={clipPath}
      {...props}
    >
      {children}
    </AnimatedStack>
  );
}
